import { battleMessages } from '../data/dialogues.js';
import { unlockableMoves } from '../data/moves.js';

function randomPick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

function calcDodgeChance(player) {
  let dodge = player.luck * 0.5;
  if (player.passive?.type === 'dodge') {
    dodge += player.passive.value;
  }
  return Math.min(dodge, 60);
}

function getPassiveMultiplier(player) {
  if (player.passive?.type === 'crit_low_hp') {
    const threshold = player.passive.value / 100;
    if (player.hp / player.maxHp < threshold) {
      return 2;
    }
  }
  return 1;
}

function applyPassiveEndOfTurn(state) {
  const player = { ...state.player };
  const log = [...state.battleLog];

  if (player.passive?.type === 'regen') {
    const heal = player.passive.value;
    const oldHp = player.hp;
    player.hp = Math.min(player.maxHp, player.hp + heal);
    if (player.hp > oldHp) {
      log.push({ text: `Zenitude Administrative : +${player.hp - oldHp} HP regeneres.`, type: 'system' });
    }
  }

  if (player.passive?.type === 'atk_stack') {
    if (player.passiveStacks < player.passive.max) {
      player.passiveStacks += player.passive.value;
      player.atk += player.passive.value;
      log.push({ text: `Force du Nombre : +${player.passive.value} ATK (stack ${player.passiveStacks}/${player.passive.max})`, type: 'system' });
    }
  }

  if (player.passive?.type === 'def_stack') {
    if (player.passiveStacks < player.passive.max) {
      const gain = Math.min(player.passive.value, player.passive.max - player.passiveStacks);
      player.passiveStacks += gain;
      player.def += gain;
      log.push({ text: `Blindage Administratif : +${gain} DEF (total bonus: ${player.passiveStacks})`, type: 'system' });
    }
  }

  return { ...state, player, battleLog: log };
}

function processBuffs(player) {
  const updated = { ...player };
  const expiredBuffs = [];
  const activeBuffs = [];

  for (const buff of updated.buffs || []) {
    const remaining = buff.turnsLeft - 1;
    if (remaining <= 0) {
      if (buff.stat === 'atk') updated.atk -= buff.value;
      if (buff.stat === 'def') updated.def -= buff.value;
      if (buff.stat === 'luck') updated.luck -= buff.value;
      expiredBuffs.push(buff);
    } else {
      activeBuffs.push({ ...buff, turnsLeft: remaining });
    }
  }

  const activeDebuffs = [];
  for (const debuff of updated.debuffs || []) {
    const remaining = debuff.turnsLeft - 1;
    if (remaining <= 0) {
      activeDebuffs.push(null);
    } else {
      activeDebuffs.push({ ...debuff, turnsLeft: remaining });
    }
  }

  updated.buffs = activeBuffs;
  updated.debuffs = activeDebuffs.filter(Boolean);

  if (updated.healDisabledTurns > 0) {
    updated.healDisabledTurns -= 1;
    if (updated.healDisabledTurns <= 0) {
      updated.healDisabled = false;
    }
  }

  return { updated, expiredBuffs };
}

function processDots(player) {
  let totalDotDmg = 0;
  const activeDots = [];
  const messages = [];

  for (const dot of player.dots || []) {
    totalDotDmg += dot.value;
    messages.push(`Boucle infinie : -${dot.value} HP (${dot.turnsLeft - 1} tours restants)`);
    if (dot.turnsLeft - 1 > 0) {
      activeDots.push({ ...dot, turnsLeft: dot.turnsLeft - 1 });
    }
  }

  return { totalDotDmg, activeDots, messages };
}

export function playerAttack(state, move) {
  let player = { ...state.player };
  let boss = { ...state.boss };
  const log = [...state.battleLog];
  let newScreen = state.screen;

  if (player.skipNextTurn) {
    player.skipNextTurn = false;
    log.push({ text: "Tu es bloque a chercher le bon formulaire... Tour perdu !", type: 'system' });
    return {
      ...state,
      player,
      battleLog: log,
      turn: 'boss',
      isAnimating: true
    };
  }

  if (move.type === 'defense') {
    const boost = move.defBoost || 8;
    player.buffs = [...(player.buffs || []), { stat: 'def', value: boost, turnsLeft: 2 }];
    player.def += boost;
    log.push({ text: `${move.emoji} ${move.name} -- ${randomPick(battleMessages.defenseMessages)}`, type: 'player' });

    return {
      ...state,
      player,
      battleLog: log,
      turn: 'boss',
      isAnimating: true
    };
  }

  if (move.type === 'heal') {
    if (player.healDisabled) {
      log.push({ text: "Soins bloques ! Tes droits sont suspendus !", type: 'system' });
      return {
        ...state,
        player,
        battleLog: log,
        turn: 'boss',
        isAnimating: true
      };
    }
    const healAmt = move.healAmount || 20;
    const oldHp = player.hp;
    player.hp = Math.min(player.maxHp, player.hp + healAmt);
    const healed = player.hp - oldHp;
    log.push({ text: `${move.emoji} ${move.name} -- +${healed} HP ! ${randomPick(battleMessages.healMessages)}`, type: 'player' });

    return {
      ...state,
      player,
      battleLog: log,
      turn: 'boss',
      isAnimating: true
    };
  }

  // Attack type
  const roll = Math.random() * 100;
  if (roll > move.accuracy) {
    log.push({ text: `${move.emoji} ${move.name} -- ${randomPick(battleMessages.playerMiss)}`, type: 'player' });
    return {
      ...state,
      player,
      battleLog: log,
      turn: 'boss',
      isAnimating: true
    };
  }

  let baseDmg = move.damage * (1 + Math.random() * 0.3);
  const passiveMulti = getPassiveMultiplier(player);
  baseDmg *= passiveMulti;

  if (player.nextAttackDouble) {
    baseDmg *= 2;
    player.nextAttackDouble = false;
    log.push({ text: "ATTAQUE DOUBLEE ! Le courrier en double joue en ta faveur !", type: 'critical' });
  }

  // Add player ATK scaling
  baseDmg += player.atk * 0.5;

  const defense = boss.def * 0.3;
  let finalDmg = Math.max(1, Math.round(baseDmg - defense));

  const isCrit = Math.random() < (player.luck * 0.02);
  if (isCrit) {
    finalDmg = Math.round(finalDmg * 1.8);
    log.push({ text: `${move.emoji} ${move.name} -- ${randomPick(battleMessages.playerCrit)} (-${finalDmg} HP)`, type: 'critical' });
  } else {
    log.push({ text: `${move.emoji} ${move.name} -- ${randomPick(battleMessages.playerHit)} (-${finalDmg} HP)`, type: 'player' });
  }

  if (passiveMulti > 1) {
    log.push({ text: "Desespoir Productif active ! Degats x2 !", type: 'system' });
  }

  boss.hp = Math.max(0, boss.hp - finalDmg);

  // Apply move effects on boss
  if (move.effect) {
    if (move.effect.type === 'debuff_def') {
      boss.def = Math.max(0, boss.def - move.effect.value);
      log.push({ text: `Le mediateur affaiblit les defenses ! Boss -${move.effect.value} DEF`, type: 'system' });
    }
  }

  if (boss.hp <= 0) {
    return {
      ...state,
      player,
      boss,
      battleLog: log,
      screen: 'VICTORY',
      isAnimating: false
    };
  }

  return {
    ...state,
    player,
    boss,
    battleLog: log,
    turn: 'boss',
    isAnimating: true
  };
}

function attackMeetsCondition(attack, boss) {
  if (!attack.condition) return true;
  if (attack.condition.type === 'hp_below') {
    return boss.hp / boss.maxHp < attack.condition.value;
  }
  if (attack.condition.type === 'hp_above') {
    return boss.hp / boss.maxHp > attack.condition.value;
  }
  return true;
}

export function bossAttack(state) {
  let player = { ...state.player };
  let boss = { ...state.boss };
  const log = [...state.battleLog];

  // Pick attack - weighted by crit chance, gated by per-attack conditions
  let attack;
  const critAttacks = boss.attacks.filter(a => a.isCrit && attackMeetsCondition(a, boss));
  const normalAttacks = boss.attacks.filter(a => !a.isCrit && attackMeetsCondition(a, boss));

  const critRoll = Math.random();
  const selectedCrit = critAttacks.find(a => critRoll < (a.chance || 0));

  if (selectedCrit) {
    attack = selectedCrit;
  } else if (normalAttacks.length > 0) {
    attack = randomPick(normalAttacks);
  } else {
    // Fallback if every attack is gated out
    attack = randomPick(boss.attacks.filter(a => !a.isCrit)) || randomPick(boss.attacks);
  }

  // Check for dodge
  const dodgeChance = calcDodgeChance(player);
  if (Math.random() * 100 < dodgeChance && attack.damage > 0) {
    log.push({ text: `${attack.emoji} ${attack.name} -- ${randomPick(battleMessages.dodgeMessages)}`, type: 'boss' });

    const stateAfterDots = applyDotsAndPassive({ ...state, player, boss, battleLog: log });
    return {
      ...stateAfterDots,
      turn: 'player',
      isAnimating: false
    };
  }

  // Pick a contextual message based on what the player just did
  const reactionKey = state.lastPlayerMoveType;
  const reactionMsg = reactionKey && attack.reactions?.[reactionKey];
  const finalMsg = reactionMsg || attack.message;
  log.push({ text: `${attack.emoji} ${attack.name} -- ${finalMsg}`, type: attack.isCrit ? 'critical' : 'boss' });

  // Calculate damage
  if (attack.damage > 0) {
    let dmg = attack.damage * (1 + Math.random() * 0.2);
    const playerDef = player.def * 0.4;
    let finalDmg = Math.max(1, Math.round(dmg - playerDef));

    if (attack.isCrit) {
      finalDmg = Math.round(finalDmg * 1.5);
    }

    player.hp = Math.max(0, player.hp - finalDmg);
    log.push({ text: `Tu perds ${finalDmg} HP !`, type: 'boss' });
  }

  // Apply attack effects
  if (attack.effect) {
    switch (attack.effect.type) {
      case 'skip_turn':
        player.skipNextTurn = true;
        log.push({ text: "Tu vas perdre ton prochain tour !", type: 'system' });
        break;

      case 'self_heal': {
        const healVal = attack.effect.value;
        boss.hp = Math.min(boss.maxHp, boss.hp + healVal);
        log.push({ text: `Le boss recupere ${healVal} HP !`, type: 'system' });
        break;
      }

      case 'dot':
        player.dots = [...(player.dots || []), { value: attack.effect.value, turnsLeft: attack.effect.turns }];
        log.push({ text: `Effet de boucle infinie ! -${attack.effect.value} HP/tour pendant ${attack.effect.turns} tours.`, type: 'system' });
        break;

      case 'shuffle_moves':
        player.movesShuffled = true;
        log.push({ text: "Tes attaques sont melangees ! Bonne chance pour retrouver le bon formulaire.", type: 'system' });
        break;

      case 'disable_heal':
        player.healDisabled = true;
        player.healDisabledTurns = attack.effect.turns;
        log.push({ text: `Soins desactives pour ${attack.effect.turns} tours !`, type: 'system' });
        break;

      case 'next_double':
        player.nextAttackDouble = true;
        log.push({ text: "Attention : la prochaine attaque du boss sera doublee !", type: 'system' });
        break;

      case 'debuff_luck':
        player.luck = Math.max(0, player.luck - attack.effect.value);
        log.push({ text: `Ta chance baisse de ${attack.effect.value} !`, type: 'system' });
        break;

      case 'debuff_def':
        player.def = Math.max(0, player.def - attack.effect.value);
        log.push({ text: `Ta defense baisse de ${attack.effect.value} !`, type: 'system' });
        break;
    }
  }

  if (player.hp <= 0) {
    return {
      ...state,
      player,
      boss,
      battleLog: log,
      screen: 'DEFEAT',
      isAnimating: false
    };
  }

  const stateAfterDots = applyDotsAndPassive({ ...state, player, boss, battleLog: log });
  player = stateAfterDots.player;

  if (player.hp <= 0) {
    return {
      ...stateAfterDots,
      screen: 'DEFEAT',
      isAnimating: false
    };
  }

  return {
    ...stateAfterDots,
    turn: 'player',
    isAnimating: false
  };
}

function applyDotsAndPassive(state) {
  let player = { ...state.player };
  const log = [...state.battleLog];

  // Process DOTs
  const { totalDotDmg, activeDots, messages } = processDots(player);
  if (totalDotDmg > 0) {
    player.hp = Math.max(0, player.hp - totalDotDmg);
    messages.forEach(m => log.push({ text: m, type: 'system' }));
  }
  player.dots = activeDots;

  // Process buff expiry
  const { updated, expiredBuffs } = processBuffs(player);
  player = updated;
  expiredBuffs.forEach(b => {
    log.push({ text: `Effet ${b.stat.toUpperCase()} expire.`, type: 'system' });
  });

  // Apply passive
  const stateWithPassive = applyPassiveEndOfTurn({ ...state, player, battleLog: log });

  return stateWithPassive;
}

export function useItem(state, itemId) {
  const player = { ...state.player };
  const log = [...state.battleLog];
  const inventory = [...(player.inventory || [])];

  const itemIndex = inventory.findIndex(i => i.id === itemId && i.count > 0);
  if (itemIndex === -1) return state;

  const item = { ...inventory[itemIndex] };
  item.count -= 1;
  if (item.count <= 0) {
    inventory.splice(itemIndex, 1);
  } else {
    inventory[itemIndex] = item;
  }

  return applyItemEffect(state, itemId, player, inventory, log);
}

function applyItemEffect(state, itemId, player, inventory, log) {
  switch (itemId) {
    case 'cafe': {
      if (player.healDisabled) {
        log.push({ text: "Soins bloques ! Meme le cafe ne peut rien pour toi.", type: 'system' });
        break;
      }
      const heal = 15;
      const old = player.hp;
      player.hp = Math.min(player.maxHp, player.hp + heal);
      log.push({ text: `\u2615 Cafe de la machine -- +${player.hp - old} HP. Gout douteux, efficacite prouvee.`, type: 'player' });
      break;
    }
    case 'photocopie': {
      player.buffs = [...(player.buffs || []), { stat: 'atk', value: 5, turnsLeft: 3 }];
      player.atk += 5;
      log.push({ text: "\ud83d\udcc4 Photocopie certifiee conforme -- +5 ATK pendant 3 tours !", type: 'player' });
      break;
    }
    case 'ticket': {
      player.buffs = [...(player.buffs || []), { stat: 'def', value: 8, turnsLeft: 3 }];
      player.def += 8;
      log.push({ text: "\ud83c\udfab Ticket de file d'attente -- +8 DEF pendant 3 tours !", type: 'player' });
      break;
    }
    case 'justificatif': {
      if (player.healDisabled) {
        log.push({ text: "Soins bloques ! Ton justificatif ne sert a rien.", type: 'system' });
        break;
      }
      const heal = 25;
      const old = player.hp;
      player.hp = Math.min(player.maxHp, player.hp + heal);
      log.push({ text: `\ud83c\udfe0 Justificatif de domicile -- +${player.hp - old} HP. La preuve que tu existes.`, type: 'player' });
      break;
    }
    case 'rib': {
      player.buffs = [...(player.buffs || []), { stat: 'luck', value: 10, turnsLeft: 3 }];
      player.luck += 10;
      log.push({ text: "\ud83c\udfe6 RIB annote au crayon -- +10 LUCK pendant 3 tours !", type: 'player' });
      break;
    }
    case 'attestation': {
      // Revive effect - only meaningful if HP is very low
      player.hp = Math.min(player.maxHp, player.hp + 30);
      log.push({ text: "\u270b Attestation sur l'honneur -- +30 HP ! Tu jures que c'est vrai.", type: 'player' });
      break;
    }
    default:
      break;
  }

  player.inventory = inventory;
  return {
    ...state,
    player,
    battleLog: log,
    turn: 'boss',
    isAnimating: true
  };
}

export function checkLevelUp(player, bossMaxHp) {
  const updated = { ...player };
  const xpGained = Math.round(bossMaxHp * 0.8);
  updated.xp += xpGained;
  let leveledUp = false;
  let newMoves = [];

  while (updated.xp >= updated.xpToNext) {
    updated.xp -= updated.xpToNext;
    updated.level += 1;
    updated.xpToNext = updated.level * 100;
    leveledUp = true;

    // Stat boosts on level up
    updated.maxHp += 5;
    updated.atk += 1;
    updated.def += 1;
    updated.luck += 1;
  }

  // Check for new move unlocks
  for (const move of unlockableMoves) {
    if (move.unlockLevel <= updated.level) {
      const alreadyHas = updated.moves.some(m => m.id === move.id);
      if (!alreadyHas) {
        updated.moves.push({ ...move });
        newMoves.push(move);
      }
    }
  }

  // Full heal between bosses
  updated.hp = updated.maxHp;

  return { player: updated, xpGained, leveledUp, newMoves };
}

export function applyLoot(player, loot) {
  const updated = { ...player };

  if (loot.effect === 'all') {
    updated.atk += loot.value;
    updated.def += loot.value;
    updated.luck += loot.value;
  } else if (loot.effect === 'atk') {
    updated.atk += loot.value;
  } else if (loot.effect === 'def') {
    updated.def += loot.value;
  } else if (loot.effect === 'luck') {
    updated.luck += loot.value;
  }

  updated.loot = [...(updated.loot || []), loot];
  return updated;
}

export function shuffleMoves(moves) {
  const shuffled = [...moves];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
